import api from 'apis/config/api-service';
import { toCmsListQuery } from 'apis/config/legacy-table-query';

interface Restaurant {
  id: string | number;
  [key: string]: any;
}

interface RestaurantParams {
  [key: string]: any;
}

interface EmailData {
  id: string | number;
  restaurantId: string | number;
  [key: string]: any;
}

interface BranchLinkData {
  status: 'attach' | 'detach';
}

const cmsRestaurants = 'api/v1/cms/restaurants';
// qsupply/backend wraps single resources in { data }; some legacy Redux actions expect the bare object.
const unwrap = (body: { data: any }) => body.data;

// Legacy callers pass the sort as 'field,DIR' or ['field,DIR'].
const legacyListQuery = (params: RestaurantParams = {}, defaultLimit?: number) =>
  toCmsListQuery({ ...params, sort: Array.isArray(params.sort) ? params.sort[0] : params.sort }, defaultLimit);

export default {
  getRestaurantById: (id: string | number) => api.get(`${cmsRestaurants}/${id}`),
  updateRestaurant: (restaurant: Restaurant) =>
    api.patch(`${cmsRestaurants}/${restaurant.id}`, restaurant).then(unwrap),
  // Restaurant-linked suppliers. The branch "attach supplier" picker passes { subEntityId, subEntityType: 'branch' }
  // to leave out suppliers already on that branch.
  getRestaurantSuppliers: (resId: string | number, params: RestaurantParams = {}) => {
    const { subEntityId, subEntityType } = params.customParams ?? {};
    const query = legacyListQuery({ ...params, filters: { name: params.filters?.name } });
    if (subEntityType === 'branch' && subEntityId) query.excludeBranchId = subEntityId;
    return api.get(`${cmsRestaurants}/${resId}/suppliers`, { params: query });
  },
  attachSupplier: (supplierId: string | number, restaurantId: string | number) =>
    api.post(`${cmsRestaurants}/${restaurantId}/suppliers/${supplierId}`),
  // Also removes the supplier from all of the restaurant's branches.
  detachSupplier: (supplierId: string | number, restaurantId: string | number) =>
    api.delete(`${cmsRestaurants}/${restaurantId}/suppliers/${supplierId}`),
  deleteRestaurantUser: (userId: string | number) => api.delete(`api/v1/users/${userId}`),
  // Branch staff (managers and users) in the legacy shape { data: { branchName, users }, total, pageCount }.
  getRestaurantAdminManagersUsers: async (branchId: string | number, params: RestaurantParams = {}) => {
    const [branch, users] = await Promise.all([
      api.get(`${cmsRestaurants}/branches/${branchId}`),
      api.get(`${cmsRestaurants}/branches/${branchId}/users`, {
        params: { page: params.pagination?.page || 1, limit: params.pagination?.rowsPerPage || 15 },
      }),
    ]);
    return { ...users, data: { branchName: branch.data.name, users: users.data } };
  },
  // Branches of one restaurant (legacy filter restaurantId.eq), names flattened to the English name.
  getRestaurantBranches: async (params: RestaurantParams = {}) => {
    const restaurantId = params.filters?.restaurantId?.eq;
    const body = await api.get(`api/v1/restaurants/branches/${restaurantId}`, {
      params: legacyListQuery({ pagination: params.pagination, sort: params.sort }, 10),
    });
    return { ...body, data: body.data.map((branch: any) => ({ ...branch, name: branch.name?.en ?? branch.name })) };
  },
  getBranchSuppliers: (branchId: string | number, params: RestaurantParams = {}) =>
    api.get(`${cmsRestaurants}/branches/${branchId}/suppliers`, {
      params: legacyListQuery({ ...params, filters: { name: params.filters?.name } }),
    }),
  attachDetachSupplierToBranch: (branchId: string | number, supplierId: string | number, data: BranchLinkData) =>
    data.status === 'detach'
      ? api.delete(`${cmsRestaurants}/branches/${branchId}/suppliers/${supplierId}`)
      : api.post(`${cmsRestaurants}/branches/${branchId}/suppliers/${supplierId}`),
  // Rows carry restaurantId so the verify toggle can address the email.
  getRestaurantSupplierEmails: async (resId: string | number, suppId: string | number) => {
    const body = await api.get(`${cmsRestaurants}/${resId}/suppliers/${suppId}/emails`, {
      params: { page: 1, limit: 100 },
    });
    return { ...body, data: body.data.map((email: any) => ({ ...email, restaurantId: resId })) };
  },
  verifyEmailCC: (data: EmailData) =>
    api.patch(`${cmsRestaurants}/${data.restaurantId}/emails/${data.id}`, { hasVerifiedEmail: data.hasVerifiedEmail }),
};
